import { useState } from "react";
import { api, ApiError, query } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import type { Territory } from "../api/types";

type Report = { id: string; title: string; text: string; patrol: boolean; period: boolean; geo?: boolean };

// Реестры для выгрузки. patrol — нужен доступ к служебным данным
// (научному сотруднику доступны только наблюдения).
const REPORTS: Report[] = [
  { id: "shifts", title: "Журнал смен", text: "Смены за период: длительность, обходы, инциденты, наблюдения, расхождения по имуществу.", patrol: true, period: true },
  { id: "checkpoints", title: "Прохождение контрольных точек", text: "Сводка по инспектору и маршруту: точки по плану, пройдено, не пройдено, процент, пробелы трека.", patrol: true, period: true },
  { id: "incidents", title: "Реестр инцидентов", text: "Категории, приоритеты, статусы, ответственные, время на устройстве и время получения, координаты.", patrol: true, period: true },
  { id: "observations", title: "Реестр наблюдений", text: "Определение инспектора и научная верификация. Координаты охраняемых видов — по правам вашей роли.", patrol: false, period: true },
  { id: "equipment", title: "Имущество смен", text: "Выдача и сдача по каждому виду имущества, номера, состояние, расхождения и исправления.", patrol: true, period: true },
  { id: "devices", title: "Устройства и синхронизация", text: "Модели, версии, последняя связь, фото в очереди и отклонённые записи.", patrol: true, period: false },
  { id: "tracks", title: "Треки обходов", text: "Все обходы за период одним файлом: фактический трек по отрезкам, план маршрута, считанные метки. Массовая выгрузка — по отдельному праву.", patrol: true, period: true, geo: true },
];

/** Раздел «Отчёты»: выгрузка реестров в CSV и XLSX. Каждая выгрузка попадает в журнал аудита. */
export function ReportsPage() {
  const { me } = useAuth();
  const territories = useQuery<{ items: Territory[] }>("/api/v1/territories");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [territory, setTerritory] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!me) return null;
  // Устройства закрыты инспектору: это сведения о чужих телефонах.
  const available = REPORTS.filter((r) =>
    (!r.patrol || me.can_read_patrol_data) && (r.id !== "devices" || me.role !== "inspector") &&
    (!r.geo || me.can_bulk_export_geo));

  async function download(r: Report, format: string) {
    setBusy(`${r.id}.${format}`);
    setError(null);
    try {
      const params = r.period ? {
        from: from ? new Date(from).toISOString() : undefined,
        // Верхняя граница включает выбранный день целиком.
        to: to ? new Date(new Date(to).getTime() + 86400000).toISOString() : undefined,
        territory_id: territory,
      } : {};
      const base = r.geo ? "/api/v1/tracks" : `/api/v1/export/${r.id}`;
      await api.download(base + query({ format, ...params }), `${r.id}.${format}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось выгрузить отчёт");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Отчёты</h2>
          <p className="muted small">Выгружаются только данные, доступные вашей роли. Каждая выгрузка записывается в журнал аудита.</p>
        </div>
      </div>

      <div className="filters">
        <label className="field">С даты
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">По дату
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {(territories.data?.items.length ?? 0) > 1 && (
          <label className="field">Территория
            <select value={territory} onChange={(e) => setTerritory(e.target.value)}>
              <option value="">Все</option>
              {territories.data!.items.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
        {(from || to || territory) && (
          <button className="secondary" onClick={() => { setFrom(""); setTo(""); setTerritory(""); }}>Сбросить</button>
        )}
      </div>

      {error && <div className="error" role="alert">{error}</div>}

      <div className="grid reports">
        {available.map((r) => (
          <section key={r.id} className="card report">
            <h3>{r.title}</h3>
            <p className="muted small">{r.text}</p>
            {!r.period && <p className="muted small">Текущее состояние, без учёта периода.</p>}
            <div className="filters" style={{ margin: 0 }}>
              {(r.geo ? ["gpx", "geojson"] : ["xlsx", "csv", "pdf"]).map((f, i) => (
                <button key={f} className={i > 0 ? "secondary" : undefined} disabled={busy !== null}
                  onClick={() => void download(r, f)}>
                  {busy === `${r.id}.${f}` ? "Готовлю…" : f === "geojson" ? "GeoJSON" : f.toUpperCase()}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
